<template>
  <MatchDetailDataProvider
    :match-id="matchId"
    v-slot="{ match, loading, failed }"
  >
    <ParticipantDashboardLayout>
      <div class="p-6 md:p-8 space-y-6 w-full">
        <!-- Back Button + section navigation -->
        <div class="flex flex-col gap-6">
          <button
            type="button"
            class="self-start text-slate-500 hover:text-teal-600 text-sm font-semibold inline-flex items-center gap-2 group transition-colors"
            aria-label="Back to dashboard"
            @click="$router.push({ name: 'dashboard' }).catch(() => {})"
          >
            <i
              class="fal fa-arrow-left group-hover:-translate-x-0.5 transition-transform"
            ></i>
            <span>Back to Dashboard</span>
          </button>
          <NetworkingQuickNav />
        </div>

        <!-- Loading State -->
        <div
          v-if="loading"
          class="py-16 bg-white rounded-2xl border border-slate-200 text-center text-slate-500 shadow-sm"
        >
          <i class="fal fa-spinner-third animate-spin mr-2"></i> Loading sponsor
          details...
        </div>

        <!-- Error State -->
        <div
          v-else-if="failed || !match"
          class="py-16 bg-white rounded-2xl border border-slate-200 text-center text-red-500 px-4 shadow-sm"
        >
          {{ $t('Views.AiMatching.Matches.loadFailed') }}
        </div>

        <!-- Sponsor Details Split View -->
        <div
          v-else
          class="space-y-6 animate-fade-in"
          :data-match-id="match.matchId"
        >
          <!-- Top Premium Header Card -->
          <header
            class="bg-white rounded-2xl border border-slate-200 p-4 md:p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-6"
          >
            <div class="flex items-center gap-3 md:gap-4.5">
              <!-- Sponsor Logo Frame -->
              <div
                class="w-18 h-18 rounded-2xl bg-slate-50 border border-slate-100 p-2 flex-shrink-0 flex items-center justify-center overflow-hidden"
              >
                <img
                  v-if="match.sponsorLogoUrl"
                  :src="match.sponsorLogoUrl"
                  :alt="match.sponsorName"
                  class="w-full h-full object-contain"
                />
                <div
                  v-else
                  class="w-full h-full bg-slate-800 text-white flex items-center justify-center font-bold text-xl rounded-xl"
                >
                  {{ (match.sponsorName || '?')[0].toUpperCase() }}
                </div>
              </div>

              <div class="min-w-0">
                <h1 class="text-2xl font-black text-slate-900 leading-tight">
                  {{ match.sponsorName }}
                </h1>
                <div class="flex flex-wrap items-center gap-2 mt-2">
                  <span
                    v-if="match.sponsorBoothLabel"
                    class="text-xs text-slate-500 flex items-center gap-1"
                  >
                    <i class="fal fa-map-marker-alt"></i>
                    Booth {{ match.sponsorBoothLabel }}
                  </span>
                  <span
                    v-if="match.sponsorIndustryLabel"
                    class="text-xxs font-bold uppercase tracking-widest text-slate-400 bg-slate-50 px-2.5 py-0.5 rounded border border-slate-100"
                  >
                    {{ match.sponsorIndustryLabel }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Primary CTAs -->
            <div
              class="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:shrink-0"
            >
              <button
                @click="bookMeeting(match)"
                class="w-full sm:w-auto btn-brand font-bold text-sm px-5 py-3 rounded-xl shadow-sm transition-all duration-200 flex items-center justify-center gap-2 min-h-[44px]"
              >
                <i class="fal fa-calendar-plus"></i>
                <span>Book a Meeting</span>
              </button>
            </div>
          </header>

          <!-- Grid Splits Layout -->
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <!-- Left: Overview and Contact -->
            <div class="lg:col-span-2 space-y-6">
              <!-- Overview Description -->
              <section
                v-if="match.sponsorDescription"
                class="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm"
              >
                <h2
                  class="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"
                >
                  <i class="fal fa-file-alt text-teal-500"></i>
                  <span>{{
                    $t('Views.AiMatching.Detail.sponsorOverview')
                  }}</span>
                </h2>
                <!-- Sponsor bio is rich text (and sponsor-editable), so render it as sanitized
                     HTML rather than escaping the tags into literal "<p>" (B7). -->
                <div
                  class="text-sm text-slate-600 leading-relaxed html-content"
                  v-html="bioHtml(match.sponsorDescription)"
                ></div>
              </section>

              <!-- Resources / Contact Details. Only render when there's at least one resource;
                   otherwise the heading sat over an empty grid (B8). -->
              <section
                v-if="
                  match.sponsorWebsite ||
                  match.sponsorEmail ||
                  match.sponsorCompanyOverviewPdfUrl
                "
                class="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4"
              >
                <h2 class="text-base font-extrabold text-slate-900">
                  Resources & Details
                </h2>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <a
                    v-if="match.sponsorWebsite"
                    :href="match.sponsorWebsite"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="flex items-center gap-3 p-3.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl transition-all group"
                  >
                    <div
                      class="w-10 h-10 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center group-hover:scale-105 transition-transform shrink-0"
                    >
                      <i class="fal fa-globe text-lg"></i>
                    </div>
                    <div class="min-w-0">
                      <span
                        class="block text-xxs font-bold text-slate-400 uppercase tracking-widest"
                        >Website</span
                      >
                      <span
                        class="block text-xs font-bold text-slate-700 truncate"
                        >{{ cleanUrl(match.sponsorWebsite) }}</span
                      >
                    </div>
                  </a>

                  <a
                    v-if="match.sponsorEmail"
                    :href="'mailto:' + match.sponsorEmail"
                    class="flex items-center gap-3 p-3.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl transition-all group"
                  >
                    <div
                      class="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center group-hover:scale-105 transition-transform shrink-0"
                    >
                      <i class="fal fa-envelope text-lg"></i>
                    </div>
                    <div class="min-w-0">
                      <span
                        class="block text-xxs font-bold text-slate-400 uppercase tracking-widest"
                        >Email</span
                      >
                      <span
                        class="block text-xs font-bold text-slate-700 truncate"
                        >{{ match.sponsorEmail }}</span
                      >
                    </div>
                  </a>

                  <a
                    v-if="match.sponsorCompanyOverviewPdfUrl"
                    :href="match.sponsorCompanyOverviewPdfUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="flex items-center gap-3 p-3.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl transition-all group sm:col-span-2"
                  >
                    <div
                      class="w-10 h-10 rounded-lg bg-red-50 text-red-600 flex items-center justify-center group-hover:scale-105 transition-transform shrink-0"
                    >
                      <i class="fal fa-file-pdf text-lg"></i>
                    </div>
                    <div class="min-w-0">
                      <span
                        class="block text-xxs font-bold text-slate-400 uppercase tracking-widest"
                        >Brochure</span
                      >
                      <span
                        class="block text-xs font-bold text-slate-700 truncate"
                        >Download Company Overview PDF</span
                      >
                    </div>
                  </a>
                </div>
              </section>
            </div>

            <!-- Right: Why We Matched Gauge & Breakdown -->
            <div class="space-y-6">
              <!-- Sleek Dark Score Compatibility Card -->
              <div
                class="bg-slate-900 text-white rounded-2xl p-6 shadow-sm space-y-6 relative overflow-hidden"
              >
                <!-- Soft Glow Accent -->
                <div
                  class="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-teal-400 via-teal-600 to-transparent pointer-events-none"
                ></div>

                <div
                  class="relative z-10 flex flex-col items-center text-center space-y-3"
                >
                  <span
                    class="text-xxs font-extrabold tracking-widest text-teal-400 uppercase"
                    >Match Compatibility</span
                  >

                  <!-- Circular Gauge -->
                  <div
                    class="relative w-28 h-28 flex items-center justify-center rounded-full bg-slate-800 border-4 border-slate-800 shadow-inner"
                  >
                    <div
                      class="absolute inset-0 rounded-full border-2 border-dashed border-teal-400/40 animate-[spin_25s_linear_infinite]"
                    ></div>
                    <div class="flex flex-col items-center">
                      <span class="text-3xl font-black text-teal-400"
                        >{{ displayScore(match) }}%</span
                      >
                      <span
                        class="text-[9px] uppercase tracking-wider font-bold text-slate-400"
                        >Match</span
                      >
                    </div>
                  </div>
                </div>

                <div
                  class="relative z-10 border-t border-slate-850 pt-5 space-y-4"
                >
                  <h3
                    class="text-xs font-bold text-white uppercase tracking-wider"
                  >
                    Why You Matched
                  </h3>
                  <!-- Prefer the LLM-generated explanation from the matching campaign when it
                       exists (only top-N matches get one). Otherwise fall back to a deterministic
                       sentence built from the criteria breakdown so the panel never shows just a
                       heading with no body. -->
                  <p
                    v-if="match.explanation"
                    class="text-xs text-slate-300 leading-relaxed italic"
                  >
                    "{{ match.explanation }}"
                  </p>
                  <p
                    v-else-if="explanationFromCriteria(match)"
                    class="text-xs text-slate-300 leading-relaxed"
                  >
                    {{ explanationFromCriteria(match) }}
                  </p>

                  <!-- Criteria Breakdown bars -->
                  <div
                    v-if="orderedCriteria(match).length"
                    class="space-y-3 pt-2"
                  >
                    <div
                      v-for="row in orderedCriteria(match)"
                      :key="row.key"
                      class="space-y-1.5"
                      :class="row.rowClass"
                    >
                      <div
                        class="flex items-center justify-between text-xxs font-semibold"
                      >
                        <span class="text-slate-400 uppercase tracking-wider">
                          {{ row.label }}
                          <span v-if="row.notCounted" class="criterion-tag">
                            {{ notCountedLabel }}
                          </span>
                        </span>
                        <span class="font-bold" :class="row.pctClass"
                          >{{ row.percent }}%</span
                        >
                      </div>
                      <div
                        class="w-full bg-slate-800 h-1 rounded-full overflow-hidden"
                      >
                        <div
                          class="h-1 rounded-full"
                          :class="row.fillClass"
                          :style="{ width: row.percent + '%' }"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ParticipantDashboardLayout>
  </MatchDetailDataProvider>
</template>

<script>
import BookMeetingModal from '@/components/sponsor/BookMeetingModal';
import ParticipantDashboardLayout from '@/components/shared/ParticipantDashboardLayout.vue';
import NetworkingQuickNav from '@/components/aiMatching/NetworkingQuickNav.vue';
import { sanitizeHtml } from '@/helpers/sanitize';
import {
  displayScore,
  isCriterionNotCounted,
  MATCH_TIER_HIGH,
  MATCH_TIER_MID
} from '@/helpers/match-score';
import { englishOnlyI18nMixin } from '@/mixins';

const CRITERION_KEYS = [
  'industryAlignment',
  'interestOverlap',
  'networkingGoals',
  'budgetAlignment',
  'decisionLevelMatch',
  'solutionCategoryMatch',
  'organizationTypeMatch'
];

export default {
  name: 'AiMatchSponsor',
  components: {
    ParticipantDashboardLayout,
    NetworkingQuickNav
  },
  mixins: [englishOnlyI18nMixin],
  props: {
    matchId: { type: String, required: true }
  },
  computed: {
    notCountedLabel() {
      return this.$t('Views.AiMatching.Detail.notCounted');
    }
  },
  methods: {
    bioHtml(description) {
      return sanitizeHtml(description);
    },
    displayScore(match) {
      return displayScore(match.calibratedScore);
    },
    cleanUrl(url) {
      if (!url) return '';
      return url.replace(/https?:\/\/(www\.)?/, '').split('/')[0];
    },
    orderedCriteria(match) {
      const subScores = match.subScores || {};
      const weights = match.criterionWeights || null;
      return CRITERION_KEYS.filter((k) => typeof subScores[k] === 'number')
        .map((k) => {
          // Weighted to 0 by the organizer, so it contributed nothing to the headline. Still
          // shown (hiding it reads as missing data) but dimmed, flagged, and rendered at 0 —
          // showing its raw similarity is what made people conclude a match was driven by a
          // criterion that was switched off. MUST stay identical to the sponsor portal
          // (ems LeadDetailModal.jsx): a sponsor and a participant comparing screens have to
          // see the same number for the same criterion.
          const notCounted = isCriterionNotCounted(weights, k);
          return {
            key: k,
            label: this.$t('Views.AiMatching.Detail.' + k),
            // Sub-scores are stored 0-100 — never rescale. The old guard turned a genuine
            // low score (a three-rung ladder miss scores ~1.1) into 110%.
            percent: notCounted ? 0 : displayScore(subScores[k]),
            notCounted: notCounted,
            // Classes resolved here rather than with template ternaries, to keep the markup
            // readable and avoid long lines the formatter fights over.
            rowClass: notCounted ? 'opacity-50' : '',
            pctClass: notCounted ? 'text-slate-400' : 'text-teal-400',
            fillClass: notCounted ? 'bg-slate-600' : 'bg-teal-400'
          };
        })
        .sort((a, b) => b.percent - a.percent);
    },
    // Build a short, deterministic explanation from the criteria breakdown for matches whose
    // LLM-generated explanation didn't land (rows below the configured topN never get one;
    // the LLM may also be disabled in some environments). The criteria labels themselves
    // carry the specificity ("Industry alignment", "Interest overlap", etc.) and are
    // already translated, so we just stitch the top contributors into a sentence.
    explanationFromCriteria(match) {
      const overall = this.displayScore(match);
      // Only criteria that actually counted may justify the score — quoting a switched-off
      // criterion as a reason is precisely the misreading this release fixes.
      const criteria = this.orderedCriteria(match).filter(
        (c) => c.percent > 0 && !c.notCounted
      );
      if (criteria.length === 0) return '';

      const sponsorName = match.sponsorName || 'this sponsor';
      const strengthLead =
        overall >= MATCH_TIER_HIGH
          ? `You and ${sponsorName} are a strong match`
          : overall >= MATCH_TIER_MID
          ? `You and ${sponsorName} look like a promising match`
          : `You and ${sponsorName} have some common ground`;

      const top = criteria.slice(0, 2);
      const reasons =
        top.length === 1
          ? `${top[0].label.toLowerCase()} (${top[0].percent}%)`
          : `${top[0].label.toLowerCase()} (${
              top[0].percent
            }%) and ${top[1].label.toLowerCase()} (${top[1].percent}%)`;

      const tail =
        criteria.length > 2
          ? ` ${criteria[2].label} also adds to the score.`
          : '';

      return `${strengthLead} — the top contributors are ${reasons}.${tail} Overall compatibility comes in at ${overall}%.`;
    },
    bookMeeting(match) {
      if (!match || !match.sponsorId) return;
      document.body.classList.add('overflow-hidden');
      this.$modal.show(
        BookMeetingModal,
        {
          sponsorId: match.sponsorId,
          sponsorName: match.sponsorName || '',
          boothLabel: match.sponsorBoothLabel || '',
          english: true
        },
        {
          adaptive: true,
          minWidth: 320,
          maxWidth: 600,
          width: '90%',
          height: 'auto'
        }
      );
    }
  }
};
</script>

<style scoped>
/* "Not counted" badge on a criterion the organizer weighted to 0. */
.criterion-tag {
  background-color: #334155;
  /* slate-700 */
  border-radius: 0.25rem;
  color: #cbd5e1;
  /* slate-300 */
  letter-spacing: normal;
  margin-left: 0.375rem;
  padding: 0.125rem 0.375rem;
  text-transform: none;
  white-space: nowrap;
}

.w-18 {
  width: 4.5rem !important;
  height: 4.5rem !important;
  min-width: 4.5rem !important;
  min-height: 4.5rem !important;
}

.h-18 {
  height: 4.5rem !important;
}

.text-xxs {
  font-size: 0.65rem;
}

.bg-slate-850 {
  background-color: #18202e;
}

.animate-fade-in {
  animation: fadeIn 0.3s ease-out forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Strictly prevent FontAwesome icons/SVGs from expanding enormously */
i.fal,
i.fas,
i.far,
i.fab,
svg {
  width: 1.125rem !important;
  height: 1.125rem !important;
  min-width: 1.125rem !important;
  min-height: 1.125rem !important;
  max-width: 1.125rem !important;
  max-height: 1.125rem !important;
  font-size: 1.125rem !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0 !important;
}

/* Spinner Sizing */
.fa-spinner-third {
  width: 1.25rem !important;
  height: 1.25rem !important;
  min-width: 1.25rem !important;
  min-height: 1.25rem !important;
  font-size: 1.25rem !important;
}

/* Resource Box Icons Size limits */
.w-10 {
  width: 2.5rem !important;
  height: 2.5rem !important;
  min-width: 2.5rem !important;
  min-height: 2.5rem !important;
  flex-shrink: 0 !important;
}

.w-10 i,
.w-10 svg {
  width: 1.25rem !important;
  height: 1.25rem !important;
  min-width: 1.25rem !important;
  min-height: 1.25rem !important;
  max-width: 1.25rem !important;
  max-height: 1.25rem !important;
  font-size: 1.25rem !important;
}

/* Circular score gauge dimensions guard */
.w-28 {
  width: 7rem !important;
  height: 7rem !important;
  min-width: 7rem !important;
  min-height: 7rem !important;
  max-width: 7rem !important;
  max-height: 7rem !important;
}
</style>
